import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Globe, Lock, BookMarked, Camera } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { isRenderableCardData } from "../components/WeeklyItemCardThumb";
import FollowButton from "../components/FollowButton";
import MessageButton from "../components/MessageButton";
import FollowersModal from "../components/FollowersModal";
import AvatarUploader from "../components/AvatarUploader";

const REACTION_LABEL = {
  resonates: "Resonates",
  questions: "Questions it",
  expands:   "Expands it",
};

export default function PublicProfile() {
  const { userId } = useParams();
  const { user: me, refresh: refreshAuth } = useAuth();
  const [data, setData] = useState(null);
  const [shelves, setShelves] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [followModal, setFollowModal] = useState(null); // "followers" | "following" | null
  const [followCounts, setFollowCounts] = useState({ followers_count: 0, following_count: 0 });
  const [uploaderOpen, setUploaderOpen] = useState(false);
  const allSavedRef = useRef(null);
  const collectionsRef = useRef(null);
  const reactionsRef = useRef(null);
  const submissionsRef = useRef(null);

  useEffect(() => {
    if (!userId) return;
    api.get(`/users/${userId}/profile`)
      .then(({ data }) => setData(data))
      .catch(() => setNotFound(true));
    api.get(`/users/${userId}/shelves`)
      .then(({ data }) => setShelves(Array.isArray(data) ? data : []))
      .catch(() => setShelves([]));
    api.get(`/users/${userId}/follow-state`)
      .then(({ data }) => setFollowCounts({
        followers_count: data.followers_count || 0,
        following_count: data.following_count || 0,
      }))
      .catch(() => {});
  }, [userId]);
  if (notFound) return <Navigate to="/" replace />;
  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-40 text-center text-muted-foreground italic serif text-xl">
        Unsealing the shelf…
      </div>
    );
  }

  const initial = (data.name || "?")[0]?.toUpperCase();

  const scrollTo = (ref) => {
    if (ref.current) ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Stat tiles. Counts of 0 render as muted, non-clickable. Both "pins" and
  // "oracle" tiles scroll to the unified All-saved-items section — those two
  // are the components of that list.
  const stats = [
    { key: "pins",          value: data.pins.length,                  label: "pins",          onClick: () => scrollTo(allSavedRef) },
    { key: "reactions",     value: data.reactions.length,             label: "reactions",     onClick: () => scrollTo(reactionsRef) },
    { key: "contributions", value: data.submissions.length,           label: "contributions", onClick: () => scrollTo(submissionsRef) },
    { key: "oracle",        value: (data.oracle_saves || []).length,  label: "oracle saves",  onClick: () => scrollTo(allSavedRef) },
    { key: "followers",     value: followCounts.followers_count,      label: "followers",     onClick: () => setFollowModal("followers") },
    { key: "following",     value: followCounts.following_count,      label: "following",     onClick: () => setFollowModal("following") },
  ];

  // Unified "All saved items" list — pins + oracle saves, chronological.
  // Reactions are marginalia (lighter than a save); submissions are the
  // user's OWN contributions, not something they've kept — both live in
  // their own smaller sections below.
  const savedItems = [
    ...data.pins.map((p) => ({ kind: "pin", id: `pin-${p.concept_id}`, created_at: p.created_at || "", data: p })),
    ...(data.oracle_saves || []).map((s) => ({ kind: "oracle", id: `oracle-${s.id}`, created_at: s.created_at || "", data: s })),
  ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  return (
    <div data-testid="public-profile" className="max-w-5xl mx-auto px-6 sm:px-10 py-14 sm:py-20">
      {/* HEADER — stacks vertically on mobile, side-by-side from `sm` up. */}
      <header className="mb-12 sm:mb-16">
        <div className="flex flex-col sm:flex-row sm:items-start gap-6">
          {data.is_self ? (
            <button
              type="button"
              data-testid="profile-avatar-edit"
              onClick={() => setUploaderOpen(true)}
              title="Change your profile picture"
              aria-label="Change your profile picture"
              className="relative group flex-shrink-0 rounded-full focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
            >
              {data.picture
                ? <img src={data.picture} alt="" className="w-20 h-20 rounded-full ring-1 ring-border object-cover" />
                : <div className="w-20 h-20 rounded-full bg-[hsl(var(--accent)/0.35)] grid place-items-center serif text-3xl text-foreground">{initial}</div>}
              <span className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                <Camera size={18} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.8} />
              </span>
            </button>
          ) : (
            data.picture
              ? <img src={data.picture} alt="" className="w-20 h-20 rounded-full ring-1 ring-border flex-shrink-0 object-cover" />
              : <div className="w-20 h-20 rounded-full bg-[hsl(var(--accent)/0.35)] grid place-items-center serif text-3xl text-foreground flex-shrink-0">{initial}</div>
          )}
          <div className="flex-1 min-w-0">
            <div className="label mb-2 text-[hsl(var(--accent))]">A reader on Correlate</div>
            <h1 className="serif text-4xl sm:text-5xl text-foreground leading-tight break-words">{data.name}</h1>
            {data.handle && (
              <div className="mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground mt-2">
                @{data.handle}
              </div>
            )}
            {data.bio && (
              <p
                data-testid="profile-bio"
                className="serif italic text-foreground/85 leading-relaxed mt-5 max-w-2xl whitespace-pre-wrap text-[16px]"
              >
                {data.bio}
              </p>
            )}
          </div>
        </div>

        {/* ACTION BUTTONS — own row beneath the avatar/name block on mobile. */}
        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <MessageButton
            slug={data.handle || data.user_id}
            ownerUserId={data.user_id}
            variant="pill"
            testid="profile-message-btn"
          />
          <FollowButton slug={data.handle || data.user_id} />
        </div>

        {/* STAT TILES — tap-targets that scroll to the relevant section or open
            a followers/following modal. Zero-counts stay visible but inactive. */}
        <ul data-testid="profile-stats" className="mt-8 grid grid-cols-3 sm:grid-cols-6 gap-px bg-border hairline">
          {stats.map((s) => {
            const isZero = !s.value;
            const Tag = isZero ? "div" : "button";
            return (
              <li key={s.key} className="bg-card">
                <Tag
                  type={isZero ? undefined : "button"}
                  onClick={isZero ? undefined : s.onClick}
                  data-testid={`profile-stat-${s.key}`}
                  disabled={isZero || undefined}
                  className={`w-full block text-left p-4 transition-colors ${
                    isZero
                      ? "opacity-50 cursor-default"
                      : "hover:bg-[hsl(var(--accent)/0.05)] cursor-pointer"
                  }`}
                >
                  <div className="serif text-2xl text-foreground tabular-nums leading-none">{s.value}</div>
                  <div className="mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground mt-2">
                    {s.label}
                  </div>
                </Tag>
              </li>
            );
          })}
        </ul>
      </header>

      {/* COLLECTIONS — named groupings first, they're the curated layer. */}
      {shelves.length > 0 && (
        <section ref={collectionsRef} data-testid="profile-shelves" className="mb-20 scroll-mt-24">
          <div className="label mb-6 flex items-center gap-2"><BookMarked size={12} /> Collections</div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {shelves.map((s) => (
              <li key={s.id} data-testid={`profile-shelf-${s.id}`}>
                <Link
                  to={`/shelf/${data.handle || data.user_id}/${s.slug}`}
                  className="block hairline p-5 hover:border-[hsl(var(--primary))] transition-colors group"
                >
                  <h3 className="serif text-xl text-foreground group-hover:text-[hsl(var(--primary))]">{s.name}</h3>
                  {s.description && (
                    <p className="serif italic text-muted-foreground mt-2 leading-relaxed line-clamp-2">{s.description}</p>
                  )}
                  <div className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-3">
                    {s.item_count ?? s.pin_count ?? 0} item{((s.item_count ?? s.pin_count ?? 0) !== 1) ? "s" : ""}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* PRIVATE PROFILE NOTICE — shown only to other viewers.
          Owners always see their own shelf; when their profile is set to
          private we show a compact reminder inside the shelf instead. */}
      {data.is_public === false && !data.is_self && (
        <div data-testid="profile-private-notice" className="paper p-8 text-center mb-10">
          <Lock size={18} className="mx-auto text-muted-foreground/70 mb-3" strokeWidth={1.6} />
          <p className="serif italic text-muted-foreground text-lg">
            This reader keeps their shelf private. You can still send them a message or read their bio above.
          </p>
        </div>
      )}

      {/* SHELVES, PINS, REACTIONS, SUBMISSIONS, ORACLE.
          Visible when the profile is public OR the viewer is the owner. */}
      {(data.is_public !== false || data.is_self) && (
        <>
          {/* Owner-only reminder: this shelf is private to everyone else. */}
          {data.is_self && data.is_public === false && (
            <div data-testid="profile-private-owner-hint" className="mb-10 flex items-center gap-3 paper p-4 border-dashed">
              <Lock size={14} className="text-muted-foreground/70 flex-shrink-0" strokeWidth={1.6} />
              <p className="mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                Only you can see this. Your shelf is set to private —
                <Link to="/settings#settings" className="link-underline text-[hsl(var(--primary))] ml-1">
                  make it public
                </Link>
                &nbsp;to share it.
              </p>
            </div>
          )}
          {/* ALL SAVED ITEMS — pins + oracle saves, chronological. Everything
              the user has explicitly kept, in one stream. */}
          <section ref={allSavedRef} data-testid="profile-all-saved" className="mb-20 scroll-mt-24">
            <div className="label mb-6 flex items-center gap-2"><BookMarked size={12} /> All saved items</div>
            {savedItems.length === 0 ? (
              <p className="serif italic text-muted-foreground">Nothing kept yet.</p>
            ) : (
              <ul className="space-y-px bg-border">
                {savedItems.map((item) => item.kind === "pin" ? (
                  <li key={item.id} data-testid={`profile-saved-pin-${item.data.concept_id}`} className="bg-card p-6">
                    <div className="flex items-center gap-2 mb-2">
                      {item.data.visibility === "private"
                        ? <Lock size={11} className="text-muted-foreground" />
                        : <Globe size={11} className="text-[hsl(var(--primary))]" />}
                      <span className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                        {item.data.visibility === "private" ? "pinned · private" : "pinned concept"}
                      </span>
                    </div>
                    <Link to={`/concept/${item.data.concept_id}`} className="serif text-2xl text-foreground hover:text-[hsl(var(--primary))] transition-colors leading-tight">
                      {item.data.concept_title}
                    </Link>
                    {item.data.note && <p className="mt-3 text-sm text-muted-foreground italic">&ldquo;{item.data.note}&rdquo;</p>}
                  </li>
                ) : (
                  <li key={item.id} data-testid={`profile-saved-oracle-${item.data.id}`} className="bg-card p-6">
                    <div className="flex items-center gap-2 mb-2">
                      {item.data.visibility === "private"
                        ? <Lock size={11} className="text-muted-foreground" />
                        : <Globe size={11} className="text-[hsl(var(--primary))]" />}
                      <span className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                        {item.data.visibility === "private" ? "from the oracle · private" : "from the oracle"}
                      </span>
                    </div>
                    <div className="serif text-2xl text-foreground leading-snug">{item.data.title}</div>
                    {item.data.bullets && item.data.bullets.length > 0 && !isRenderableCardData(item.data.card_data) && (
                      <ul className="mt-3 space-y-1">
                        {item.data.bullets.slice(0, 4).map((b, i) => (
                          <li key={`${item.id}-b-${i}`} className="flex gap-2 text-sm text-muted-foreground leading-relaxed">
                            <span className="text-[hsl(var(--primary))] mt-0.5">◇</span>
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* MARGINALIA — reactions are lighter than a save (an emoji-response,
              not a kept item), so they get their own smaller section below. */}
          <section ref={reactionsRef} data-testid="profile-reactions" className="mb-20 scroll-mt-24">
            <div className="label mb-6">Marginalia</div>
            {data.reactions.length === 0 ? (
              <p className="serif italic text-muted-foreground">No reactions yet.</p>
            ) : (
              <ul className="space-y-3">
                {data.reactions.map((r, i) => (
                  <li key={`${r.concept_id}-${r.reaction}-${i}`} data-testid={`profile-reaction-${r.concept_id}`} className="flex flex-wrap items-baseline gap-3 hairline-b pb-3">
                    <span className="mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--primary))]">{REACTION_LABEL[r.reaction] || r.reaction}</span>
                    <Link to={`/concept/${r.concept_id}`} className="serif text-xl text-foreground hover:text-[hsl(var(--primary))] transition-colors">
                      {r.concept_title}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* CONTRIBUTED CORRELATIONS — the reader's OWN work, distinct from
              anything they've saved. Kept as a small trailing section. */}
          {data.submissions.length > 0 && (
            <section ref={submissionsRef} data-testid="profile-submissions" className="scroll-mt-24">
              <div className="label mb-6">Contributed correlations</div>
              <ul className="space-y-6">
                {data.submissions.map((s) => (
                  <li key={s.id} data-testid={`profile-submission-${s.id}`} className="hairline-b pb-6">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`tag tag-${s.source_type || "esoteric"}`}>{s.source_type}</span>
                    </div>
                    <div className="serif text-2xl text-foreground">{s.title}</div>
                    {s.description && <p className="mt-2 text-muted-foreground leading-relaxed">{s.description}</p>}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {followModal && (
        <FollowersModal
          slug={data.handle || data.user_id}
          mode={followModal}
          onClose={() => setFollowModal(null)}
        />
      )}

      {data.is_self && (
        <AvatarUploader
          open={uploaderOpen}
          onClose={() => setUploaderOpen(false)}
          canReset={!!me?.has_google_picture}
          onUploaded={(url) => {
            setData((d) => (d ? { ...d, picture: url } : d));
            refreshAuth?.();
          }}
        />
      )}
    </div>
  );
}
